import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/team/accept/${token}/preview`)
      .then((r) => {
        setPreview(r.data);
        setName(r.data.name || "");
      })
      .catch((e) => setPreviewErr(e.response?.data?.detail || "This invite is invalid or expired"));
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    try {
      setSubmitting(true);
      const payload = { token };
      if (!preview?.user_exists) {
        if (!password || password.length < 8) return toast.error("Password must be at least 8 characters");
        payload.password = password;
        payload.name = name.trim();
      }
      const { data } = await axios.post(`${API}/admin/team/accept`, payload);
      if (refresh) await refresh();
      setDone(true);
      toast.success(`Welcome to the EasyStay admin team, ${data.email}`);
      setTimeout(() => nav("/admin"), 1500);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (previewErr) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="invite-error">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Invite unavailable</h1>
        <p className="mt-2 text-slate-600">{previewErr}</p>
        <Link to="/" className="mt-6 inline-block text-copper hover:underline">← Back to EasyStay</Link>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center text-slate-500" data-testid="invite-loading">
        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        <div className="mt-3">Verifying invitation…</div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="invite-accepted">
        <CheckCircle2 className="mx-auto h-12 w-12 text-forest" />
        <h1 className="mt-4 font-display text-2xl font-bold">You&apos;re in</h1>
        <p className="mt-2 text-slate-600">Redirecting to the admin panel…</p>
      </div>
    );
  }

  const needsLogin = preview.user_exists && !user;
  const needsPasswordUser = user && preview.user_exists && preview.email.toLowerCase() !== user.email?.toLowerCase();

  return (
    <div className="mx-auto max-w-lg px-6 py-12" data-testid="invite-accept">
      <div className="rounded-2xl border border-slate-200 bg-white p-8 card-shadow">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-copper" />
          <div className="text-xs uppercase tracking-widest text-slate-500">Admin invitation</div>
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Join the EasyStay admin team</h1>
        <p className="mt-2 text-sm text-slate-600">
          You&apos;ve been invited by <span className="font-semibold">{preview.invited_by_email || "the EasyStay super admin"}</span> to manage
          <span> </span>
          <span className="font-semibold">{preview.email}</span>.
        </p>
        <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Roles</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(preview.roles || []).map((r) => (
              <span key={r} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                r === "full" ? "bg-forest text-white" : "bg-white text-slate-700 border border-slate-200"
              }`}>{r}</span>
            ))}
          </div>
        </div>

        {needsLogin ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            An EasyStay account with <b>{preview.email}</b> already exists. Please sign in first, then click the invite link again.
            <div className="mt-3">
              <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="btn-primary text-sm">
                Sign in
              </Link>
            </div>
          </div>
        ) : needsPasswordUser ? (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="invite-email-mismatch">
            You&apos;re signed in as <b>{user.email}</b> but this invite is for <b>{preview.email}</b>. Please sign out first and try again.
          </div>
        ) : (
          <form onSubmit={accept} className="mt-6 space-y-3">
            {!preview.user_exists && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Your name</label>
                  <input
                    data-testid="accept-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Choose a password</label>
                  <input
                    data-testid="accept-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                  />
                </div>
              </>
            )}
            <button
              type="submit"
              data-testid="accept-submit-btn"
              disabled={submitting}
              className="btn-primary flex w-full items-center justify-center gap-2 text-sm disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submitting ? "Joining…" : "Accept and join the team"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
